import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';

import DeleteModal from '@atom/components/common/DeleteModal';
import addItemIcon from '@atom/components/common/svgIcons/add_subItem.svg';
import SchemaDetailContext from '@atom/components/schemaDetail/SchemaDetailContext';
import {
  ELEMENT_CREATE,
  ELEMENT_DELETE,
  ELEMENT_UPDATE,
} from '@atom/graph/schema';
import { Icon, IconButton, List, Tooltip } from '@atom/mui';
import {
  ArrowDropDownIcon,
  ArrowRightIcon,
  DeleteIcon,
  EditIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { SchemaTree } from '@atom/types/schema';
import { getDataManagementElementIcon } from '@atom/utilities/iconUtilities';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AddItemModal from '../AddItemModal';
import EditItemModal from '../EditItemModal';

import ListItemWrapper from './ListItemWrapper';

import classes from './subItemTree.module.css';

const { ListItemText } = List;

interface Props {
  element: SchemaTree;
  level: number;
  path: any[];
}

const ElementRow = ({ element, level, path }: Props) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const subtextRef = useRef<HTMLDivElement>(null);

  const {
    schemaTree,
    selectedSubItem,
    collapsedSubItems,
    setCollapsedSubItems,
    refetchSchemaTree,
    setSelectedSubItemPath,
    setSelectedSubItem,
    setSelectedAttribute,
    setSelectedAttributeGroup,
    setSelectedAttributeRoute,
  } = useContext(SchemaDetailContext);

  const [hovering, setHovering] = useState<boolean>(false);
  const [openSubItemCreate, setOpenSubItemCreate] = useState<boolean>(false);
  const [openSubItemEdit, setOpenSubItemEdit] = useState<boolean>(false);
  const [openSubItemDelete, setOpenSubItemDelete] = useState<boolean>(false);
  const [overflowing, setOverflowing] = useState<boolean>(false);

  const [createElement, { loading: creatingElement }] =
    useMutation(ELEMENT_CREATE);

  const [updateElement, { loading: updatingElement }] =
    useMutation(ELEMENT_UPDATE);

  const [deleteElement, { loading: deletingElement }] =
    useMutation(ELEMENT_DELETE);

  const attributeSubtext = useMemo(() => {
    if (isNilOrEmpty(element?.attributeGroups)) {
      return '';
    }

    const flatAttributes = element.attributeGroups.reduce((acc, group) => {
      return [...acc, ...group.attributes];
    }, []);

    const attributeNames = flatAttributes.reduce((acc, attribute) => {
      return attribute.isVisibleAsSubtext ? [...acc, attribute.name] : acc;
    }, []);

    return attributeNames.join(', ');
  }, [element]);

  useEffect(() => {
    const nameOverflowing =
      nameRef.current?.scrollWidth > nameRef.current?.offsetWidth;
    const subtextOverflowing =
      subtextRef.current?.scrollWidth > subtextRef.current?.offsetWidth;

    setOverflowing(nameOverflowing || subtextOverflowing);
  }, [element.name, attributeSubtext]);

  const handleListItemClick = () => {
    setSelectedSubItemPath(path);
    setSelectedSubItem(element);
    setSelectedAttribute(null);
    setSelectedAttributeGroup(null);
    setSelectedAttributeRoute(null);
  };

  const toggleListItem = event => {
    event?.stopPropagation();
    setCollapsedSubItems(toggleFromSet(collapsedSubItems, element?.id));
  };

  const handleSubItemCreate = async (name: string) => {
    await createElement({
      variables: {
        input: {
          rootSchemaId: schemaTree?.id,
          parentSchemaId: element?.id,
          assetType: name,
          name,
        },
      },
    });

    await refetchSchemaTree();
    setOpenSubItemCreate(false);
  };

  const handleSubItemEdit = async (name: string) => {
    await updateElement({
      variables: {
        input: {
          schemaId: element?.id,
          assetType: name,
          name,
        },
      },
    });

    await refetchSchemaTree();
    setOpenSubItemEdit(false);
  };

  const handleSubItemDelete = async () => {
    await deleteElement({
      variables: {
        id: element.id,
      },
    });

    setSelectedSubItemPath(null);
    setSelectedSubItem(null);
    setOpenSubItemDelete(false);

    await refetchSchemaTree();
  };

  const hasChildren = !isNilOrEmpty(element?.elements);
  const selected = selectedSubItem?.id === element?.id;
  const showActionButtons = (selected || hovering) && !schemaTree?.isPublished;

  const expanded = !collapsedSubItems.has(element?.id);
  const ExpandedIconComponent = expanded ? ArrowDropDownIcon : ArrowRightIcon;
  const textStyles = {
    color: selected ? colors.brand.blue : colors.neutral.dark,
  };

  const icon = getDataManagementElementIcon(element.markerId, selected, {
    paddingRight: '0.5rem',
  });

  const tooltipContent = (
    <div>
      <div>{element.name}</div>
      <div>{attributeSubtext}</div>
    </div>
  );

  const content = (
    <div className={classes.subItemContent}>
      <div className={classes.subItemName}>
        <Tooltip
          title={tooltipContent}
          placement="bottom-end"
          disableHoverListener={!overflowing}
        >
          <div>
            <div
              ref={nameRef}
              style={textStyles}
              className={classes.subItemOverflowContainer}
            >
              {element.name}
            </div>
            <div
              ref={subtextRef}
              className={clsx(
                classes.subItemOverflowContainer,
                classes.subtext,
              )}
            >
              {attributeSubtext}
            </div>
          </div>
        </Tooltip>
      </div>
      {showActionButtons && (
        <div className={classes.subItemActionButtons}>
          <IconButton
            tooltip="Add Sub Item"
            onClick={() => setOpenSubItemCreate(true)}
            size="small"
          >
            <img src={addItemIcon} />
          </IconButton>
          <IconButton
            tooltip="Rename"
            onClick={() => setOpenSubItemEdit(true)}
            size="small"
          >
            <Icon icon={EditIcon} />
          </IconButton>
          <IconButton
            tooltip="Delete"
            onClick={() => setOpenSubItemDelete(true)}
            size="small"
          >
            <Icon icon={DeleteIcon} />
          </IconButton>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <ListItemWrapper
          onClick={handleListItemClick}
          selected={selected}
          level={level}
          hasChildren={hasChildren}
        >
          <>
            {hasChildren && (
              <IconButton size="small" onClick={toggleListItem}>
                <Icon icon={ExpandedIconComponent} />
              </IconButton>
            )}
            {icon}
            <ListItemText primary={content} />
          </>
        </ListItemWrapper>
      </div>
      <AddItemModal
        open={openSubItemCreate}
        closeModal={() => setOpenSubItemCreate(false)}
        type="Sub Item"
        handleSave={handleSubItemCreate}
        isDisabled={creatingElement}
      />
      <EditItemModal
        name={element.name}
        type="Sub Item"
        handleSave={handleSubItemEdit}
        open={openSubItemEdit}
        closeModal={() => setOpenSubItemEdit(false)}
        isDisabled={updatingElement}
      />
      <DeleteModal
        open={openSubItemDelete}
        onConfirm={handleSubItemDelete}
        onCancel={() => setOpenSubItemDelete(false)}
        title="Delete Sub Item?"
        content={`Deleting ${element.name} will also delete all items under it.`}
        cancelText="Cancel"
        confirmText="Delete"
        disabled={deletingElement}
      />
    </>
  );
};

export default ElementRow;
